<template>
    <app-layout v-if="isPageReady">
        <common-page-plain>
            <div v-if="!showConfirm" class="eprescribe">
                <common-content-spacer height="medium">
                    <common-page-header
                        :heading="$t('pages.eprescribe.eprescribe.create_eprescribe_heading')"
                        :content="$t('pages.eprescribe.eprescribe.create_eprescribe_content')"
                        :isCenter="true"
                    ></common-page-header>
                    <common-steps ref="steps" :steps="steps" :currentStepIndex="currentStepIndex">
                        <template v-slot:providers>
                            <common-content-wrapper width="huge">
                                <create-e-prescribe
                                    v-if="!referralType && !showLastAddedEprescribe"
                                    @start-e-prescribe="startEprescribe"
                                ></create-e-prescribe>
                                <div v-if="showLastAddedEprescribe" class="eprescribe--added">
                                    <eprescribe-item-preview
                                        :showDeleteIcon="false"
                                        :eprescribe="ePrescribeList[ePrescribeList.length - 1]"
                                        :index="ePrescribeList.length - 1"
                                        :insurancePlans="insurancePlans"
                                        :specialities="specialities"
                                        :facilityServices="facilityServices"
                                        :states="states"
                                        @remove-item="removeItem"
                                    ></eprescribe-item-preview>
                                    <div class="eprescribe--added--button">
                                        <common-button
                                            :label="$t('common.microcopy.add_more')"
                                            type="primary"
                                            size="small"
                                            @click="showLastAddedEprescribe = false"
                                        ></common-button>
                                    </div>
                                </div>
                                <direct-e-prescribe
                                    v-if="referralType === ReferralTypeEnum.DIRECT"
                                    :practiceType="practiceType"
                                    :referralType="referralType"
                                    @cancel="referralType = null"
                                    @add-eprescribe="addEprescribe"
                                    @match-eprescribe="referralType = ReferralTypeEnum.MATCH"
                                ></direct-e-prescribe>
                                <match-e-prescribe
                                    v-if="referralType === ReferralTypeEnum.MATCH"
                                    :practiceType="practiceType"
                                    :referralType="referralType"
                                    :insurancePlans="insurancePlans"
                                    :specialities="specialities"
                                    :facilityServices="facilityServices"
                                    :states="states"
                                    @cancel="referralType = null"
                                    @add-eprescribe="addEprescribe"
                                ></match-e-prescribe>
                            </common-content-wrapper>
                        </template>
                        <template v-slot:review>
                            <eprescribe-side-preview
                                :ePrescribeList="ePrescribeList"
                                :insurancePlans="insurancePlans"
                                :specialities="specialities"
                                :facilityServices="facilityServices"
                                :states="states"
                                @go-next-step="goNextStep"
                                @close="goStepBack"
                                @remove-item="removeItem"
                            ></eprescribe-side-preview>
                        </template>
                        <template v-slot:patientInformation>
                            <common-content-wrapper width="large">
                                <referrals-new-make-form
                                    ref="referralsNewMakeForm"
                                    :submitLabel="'pages.eprescribe.eprescribe.create_eprescribe_heading'"
                                    @sent="createEprescribes($event)"
                                ></referrals-new-make-form>
                            </common-content-wrapper>
                        </template>
                    </common-steps>
                    <button
                        v-if="currentStep.name === 'providers'"
                        :disabled="!ePrescribeList.length"
                        class="side-button"
                        type="button"
                        @click="goNextStep()"
                    >
                        <common-icon name="eprescribe-review" size="gigantic"></common-icon>
                        <p :class="{ 'side-button__label': ePrescribeList.length }">{{ $t('common.microcopy.review') }}</p>
                    </button>
                    <div v-if="currentStep.name !== 'providers'" class="eprescribe--footer">
                        <common-buttons>
                            <button class="eprescribe--footer__button" @click="goStepBack()">
                                <common-icon name="arrow-left-blue" size="huge"></common-icon>
                                <p v-if="!isMobileLarge">{{ $t('common.microcopy.back') }}</p>
                            </button>
                        </common-buttons>
                    </div>
                </common-content-spacer>
            </div>
            <referrals-confirmation
                v-if="showConfirm"
                :heading="$t('pages.referrals.new.steps.confirmation.heading')"
                type="success"
            >
                <template #content>
                    <common-summary>
                        <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.patient_name')">
                            <common-user-name :user="referralInfo?.patient"></common-user-name>
                        </common-summary-item>

                        <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.diagnosis')">
                            {{ referralInfo.diagnosis }}
                        </common-summary-item>
                    </common-summary>
                </template>

                <template #footer>
                    <common-buttons>
                        <common-button
                            :label="$t('pages.auth.signup.steps.congratulations.content.button.proceed_to_dashboard')"
                            size="large"
                            type="primary"
                            @click="$router.push({ name: 'index.home' })"
                        ></common-button>
                    </common-buttons>
                </template>
            </referrals-confirmation>
        </common-page-plain>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import CreateEPrescribe from 'components/ePrescribe/CreateEPrescribe'
    import DirectEPrescribe from 'components/ePrescribe/DirectEPrescribe'
    import MatchEPrescribe from 'components/ePrescribe/MatchEPrescribe'
    import EprescribeSidePreview from 'components/ePrescribe/EprescribeSidePreview'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralsNewMakeForm from 'components/forms/ReferralsNewMakeForm'
    import SpecialitiesAPI from 'api/SpecialitiesAPI'
    import StatesAPI from 'api/StatesAPI'
    import ReferralsAPI from 'api/ReferralsAPI'
    import FacilityServicesAPI from 'api/FacilityServicesAPI'
    import EprescribeItemPreview from 'components/ePrescribe/EprescribeItemPreview'
    import ReferralsConfirmation from 'components/page-elements/ReferralsConfirmation'
    import ReferralTypeEnum from '@/enums/ReferralTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import Store from '~/store'
    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ReferralsNewPageMixin, ScreenSizeDetectionMixin],

        components: {
            AppLayout,
            CreateEPrescribe,
            DirectEPrescribe,
            MatchEPrescribe,
            EprescribeSidePreview,
            ReferralsNewMakeForm,
            EprescribeItemPreview,
            ReferralsConfirmation,
        },

        data() {
            return {
                practiceType: null,
                referralType: null,
                PracticeTypeEnum: PracticeTypeEnum,
                ReferralTypeEnum: ReferralTypeEnum,
                ePrescribeList: [],
                steps: [
                    {
                        displayName: this.$t('pages.eprescribe.eprescribe.steps.step_1'),
                        name: 'providers',
                    },
                    {
                        displayName: this.$t('pages.eprescribe.eprescribe.steps.step_2'),
                        name: 'review',
                    },
                    {
                        displayName: this.$t('pages.eprescribe.eprescribe.steps.step_3'),
                        name: 'patientInformation',
                    },
                ],
                currentStepIndex: 0,
                currentStep: {},
                showLastAddedEprescribe: false,
                referralInfo: null,
                showConfirm: false,
                insurancePlans: [],
                specialities: [],
                states: [],
                facilityServices: [],
            }
        },

        methods: {
            startEprescribe(payload) {
                this.$router.push({ query: { practiceType: payload.practiceType } })
                this.practiceType = payload.practiceType
                this.referralType = payload.referralType
            },

            goNextStep() {
                this.currentStepIndex = this.currentStepIndex + 1
                this.currentStep = this.steps[this.currentStepIndex]
            },

            goStepBack() {
                this.currentStepIndex = this.currentStepIndex - 1
                this.currentStep = this.steps[this.currentStepIndex]
            },

            removeItem(index) {
                this.showLastAddedEprescribe = false
                this.referralType = null
                this.ePrescribeList.splice(index, 1)
            },

            addEprescribe(payload) {
                this.ePrescribeList.push(payload)
                this.referralType = null
                this.showLastAddedEprescribe = true
            },

            createEprescribes(payload) {
                this.referralInfo = payload
                const request = {
                    eprescribeInfo: this.ePrescribeList.map((item) => {
                        return {
                            toUserId: item.practiceType === PracticeTypeEnum.PHYSICIAN ? item.selectedUser?.id : undefined,
                            toPracticeId: item.practiceType === PracticeTypeEnum.ANCILLARY ? item.selectedUser?.id : undefined,
                            toPracticeLocationId:
                                item.referralType === ReferralTypeEnum.DIRECT ? item.practiceLocationId : undefined,
                            insurancePlanId: item.insurancePlanId,
                            testIds:
                                item.referralType === ReferralTypeEnum.DIRECT
                                    ? item.testsList?.map((test) => test.id) || undefined
                                    : item.testIds || undefined,
                            specialityId: item.specialityId || undefined,
                            subSpecialityId: item.subSpecialityId || undefined,
                            searchBy: item.searchBy || 'zipcode',
                            zip: item.zip || undefined,
                            address: item.address,
                            city: item.city,
                            state: item.state,
                            practiceType: item.practiceType,
                            referralType: item.referralType,
                        }
                    }),
                    ...this.referralInfo,
                }
                const patientId = Number(request.patientId || this.$route.query.patientId)
                if (Number.isFinite(patientId) && patientId > 0) {
                    request.patientId = patientId
                }
                ReferralsAPI.createEprescribes(request)
                    .then(() => {
                        this.showConfirm = true
                    })
                    .catch(() => {
                        if (this.$refs.referralsNewMakeForm) {
                            this.$refs.referralsNewMakeForm.isSending = false
                        }
                    })
            },

            loadData() {
                Promise.all([
                    SpecialitiesAPI.getList(),
                    StatesAPI.getList(),
                    FacilityServicesAPI.getFacilityServiceGroupsTransform(),
                ]).then(([specialities, states, facilityServices]) => {
                    this.specialities = specialities
                    this.states = states
                    this.facilityServices = facilityServices
                })
            },
        },

        mounted() {
            this.insurancePlans = Store.getters['auth/getInsurancePlans']
            this.currentStep = this.steps[this.currentStepIndex]
            this.loadData()
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .eprescribe {
        &--footer {
            margin-top: 50px !important;

            &__button {
                display: flex;
                flex-wrap: wrap;
                max-width: 50px;
                justify-content: center;
                font-size: 14px;
                font-weight: 500;
            }
        }

        .side-button {
            float: right;
            z-index: 10;
            cursor: pointer;
            display: flex;
            flex-wrap: wrap;
            max-width: 50px;
            justify-content: center;
            font-size: 14px;
            font-weight: 500;

            &__label {
                color: $color--accent-1;
            }
        }

        &--added {
            @include breakpoint(smartphone-large) {
                width: 100%;
            }

            width: 468px;
            margin: 0 auto;

            &--item {
                width: 468px;
                background: #f3f7f9;
                border: 1px solid $color--accent-5;
                border-radius: 8px;
                margin-bottom: 32px;
            }

            &--button {
                text-align: right;
            }
        }

        &--preview {
            width: 468px;
            margin: 0 auto;
        }
    }
</style>
